import React from 'react';
import { SpinnerProps, SpinnerSize } from '../../../types/DesignSystem/Spinner';
import Typography from '../Typography/Typography';
import { withClassName } from '../cn';
import { useLiveRegionReady } from '../liveRegion';

/**
 * Spinner.
 *
 * **Why this exists rather than a move.** The design system's `Spinner/` directory
 * was empty while `SidePanel` imported `CompatioLoader` from
 * `Components/Common/components/CompatioLoader` — so the design system depended on
 * `Components/Common`, and on a lottie animation and a JSON blob with it. That
 * component was not a candidate for promotion:
 *
 * - Its container classes are **Bootstrap**, not Tailwind
 *   (`d-flex container w-10 justify-content-center align-items-center`), and
 *   `tailwind.config.js` carries a `blocklist` entry marked "remove after bootstrap
 *   is completely removed". Promoting it would have imported a dependency the repo
 *   is trying to delete straight into the foundation.
 * - It defaults to **300×300px** — the size it rendered at inside a side panel.
 * - `showLoader` is a declared prop that is **never read**: the div renders either
 *   way. It only appears in the effect's dependency list, so passing it calls
 *   `lottie.loadAnimation` again on the same container, stacking a second animation
 *   on top of the first.
 * - The animation is **never destroyed** — no cleanup, so the rAF loop outlives the
 *   component.
 * - No `role`, no label. A screen-reader user was told nothing (WCAG 4.1.3).
 *
 * The 56 files outside the design system that use `CompatioLoader` are untouched.
 * This is what the design system offers instead: CSS only, tokenised, announced.
 */

/*
 * § 220 — sizes are box-only now. The ring is an SVG scaled from one 24px
 * viewBox, so the stroke is PROPORTIONAL: 2.5 viewBox-units renders ~1.7px at
 * sm, 2.5px at md, ~4.2px at lg. The old border ring hardcoded 2/2/3px, which
 * made the small spinner relatively the heaviest of the three.
 */
const sizeClasses: Record<SpinnerSize, string> = {
  sm: 'tw-w-4 tw-h-4',
  md: 'tw-w-6 tw-h-6',
  lg: 'tw-w-10 tw-h-10',
};

const Spinner = React.forwardRef<HTMLSpanElement, SpinnerProps>(function Spinner({
  size = 'md',
  label = 'Loading',
  showLabel = false,
  tone = 'accent',
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  /* § 396 — the wait mounts WITH its status, so its words reach the accessibility tree
     ANNOUNCE_DELAY_MS after the region does: a screen reader hears them arrive (liveRegion.tsx).
     The words are in the DOM, and on screen, from the first render. */
  const ready = useLiveRegionReady();
  /*
    § 335 — the root is a `<span>`, not a `<div>`: Spinner sits inside `<button>` (Button,
    IconButton), whose content model is phrasing content only. `tw-inline-flex` already
    decided the box, so the tag changes nothing on screen. The visible label keeps its
    Typography element — changing it would drop Bootstrap's paragraph margin.
  */
  return (
    <span
      {...rest}
      ref={ref}
      /*
        `role="status"` with the label as text inside it: that combination is what
        makes a wait announced rather than silent (WCAG 4.1.3). `aria-live` is
        implicit in `status` and setting it again is redundant, not additive.
      */
      role="status"
      className={withClassName(`tw-inline-flex tw-items-center tw-gap-2`, className)}
      data-tone={tone}
      data-testid={dataTestId}
    >
      {/*
        § 220 — the arc ring (2026-09-07, promoted from the § 219 showcase
        candidate on the user's call): an SVG quarter-arc with ROUND CAPS over
        the token ring, replacing the border ring whose accent quadrant turned
        hard right-angle corners — border-drawn "arcs" cannot cap. Same token
        pair doing the same jobs: `edge` is the ring, `accent` (via
        currentColor) is the moving arc, so § 209's reasoning is untouched —
        this still cannot read on a saturated fill, and Button still swaps to
        the disabled shape while loading. Under reduced motion the arc holds
        still, exactly as the accent border-edge did.
      */}
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        /* § 297 — `inverse` paints the arc in ink-inverse and the ring as the same ink at
           30% (opacity on the circle: a var()-backed colour cannot take a `/30` modifier),
           the treatment a spinner on a filled button needs. */
        className={`${sizeClasses[size]} tw-shrink-0 ${tone === 'inverse' ? 'tw-text-ink-inverse' : 'tw-text-accent'} motion-safe:tw-animate-spin`}
        data-testid={dataTestId ? `${dataTestId}-indicator` : undefined}
      >
        <circle cx="12" cy="12" r="10" className={tone === 'inverse' ? 'tw-stroke-ink-inverse tw-opacity-30' : 'tw-stroke-edge'} strokeWidth="2.5" />
        <path
          d="M22 12a10 10 0 0 0-10-10"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </svg>
      {showLabel ? (
        /* § 346 — a `span`: the Spinner is a `<span>` (§ 335), and the variant's default
           `<p>` may not sit in one. A flex item either way, so the box is unchanged. */
        <Typography variant="body-primary" as="span" className="tw-text-ink-tertiary" aria-hidden={ready ? undefined : true}>{label}</Typography>
      ) : (
        <span className="tw-sr-only" aria-hidden={ready ? undefined : true}>{label}</span>
      )}
    </span>
  );
});

export default Spinner;
